import React, { useRef } from 'react';
import { Button } from 'antd';
import { ThunderboltFilled, LoadingOutlined, SwapOutlined, CopyOutlined, UserAddOutlined, UserDeleteOutlined, CrownOutlined } from '@ant-design/icons';
import { INTENT_TYPE } from './contracts';
import './aiCrew.css';

/**
 * IntentActionReview - Review/confirm card for every proposal that is not a new crew:
 * transfer, copy, add a person, remove a person, change the crew lead. None of them ranks worker
 * candidates (per CONTRACTS.md), so this renders the minimal READY payload instead of
 * ProposalHeader/EmployeeList/ExecuteBar, which all assume proposal.crew exists.
 * When adding a person would replace a clashing shift on one of the manager's own crews, the
 * clash is listed and the manager chooses: replace it (confirm) or keep it (cancel).
 */
const CONFIG = {
  [INTENT_TYPE.TRANSFER_OWNER]: {
    icon: <SwapOutlined />, title: 'Transfer Crew Ownership', confirm: 'Confirm Transfer', busy: 'Transferring...',
    rows: (p) => [['New Owner', p.newOwnerLabel || p.newOwnerName || p.newOwnerId]],
  },
  [INTENT_TYPE.COPY_CREW]: {
    icon: <CopyOutlined />, title: 'Copy Crew to New Date', confirm: 'Confirm Copy', busy: 'Copying...',
    rows: (p) => [['Target Date', p.targetDateLabel || p.targetDate]],
  },
  [INTENT_TYPE.ADD_TO_CREW]: {
    icon: <UserAddOutlined />, title: 'Add to Crew', confirm: 'Confirm Add', busy: 'Adding...',
    rows: (p) => [['Person', `${p.employeeName} (${p.employeeId})`]],
  },
  [INTENT_TYPE.REMOVE_FROM_CREW]: {
    icon: <UserDeleteOutlined />, title: 'Remove from Crew', confirm: 'Confirm Remove', busy: 'Removing...', danger: true,
    rows: (p) => [['Person', `${p.employeeName} (${p.employeeId})`]],
  },
  [INTENT_TYPE.SET_CREW_LEAD]: {
    icon: <CrownOutlined />, title: 'Change Crew Lead', confirm: 'Confirm Lead Change', busy: 'Saving...',
    rows: (p) => [['New Lead', p.newLeadLabel || `${p.employeeName} (${p.employeeId})`]],
  },
};

export default function IntentActionReview({ proposal, isExecuting = false, onExecute, onCancel }) {
  const isDebouncingRef = useRef(false);

  const config = proposal ? CONFIG[proposal.intentType] : null;
  if (!config) return null;

  const handleClick = () => {
    if (isDebouncingRef.current || isExecuting) return;
    isDebouncingRef.current = true;
    setTimeout(() => { isDebouncingRef.current = false; }, 1500);
    if (typeof onExecute === 'function') onExecute();
  };

  const conflicts = proposal.conflicts || [];
  const confirmLabel = conflicts.length ? 'Replace and Add' : config.confirm;

  return (
    <div className="ai-crew-intent-review" data-testid="ai-crew-intent-review">
      <div className="ai-crew-intent-review-icon">{config.icon}</div>
      <div className="ai-crew-intent-review-body">
        <div className="ai-crew-intent-review-title">{config.title}</div>
        <div className="ai-crew-intent-review-detail">
          <span className="ai-crew-intent-review-label">Crew</span>
          <span>
            {proposal.crewName || proposal.crewId}
            {proposal.crewDate ? ` · ${proposal.crewDate}` : ''}
          </span>
        </div>
        {config.rows(proposal).map(([label, value]) => (
          <div className="ai-crew-intent-review-detail" key={label}>
            <span className="ai-crew-intent-review-label">{label}</span>
            <span>{value}</span>
          </div>
        ))}
        {conflicts.length > 0 && (
          <div className="ai-crew-intent-review-detail" data-testid="intent-review-conflicts">
            <span className="ai-crew-intent-review-label">Clashes</span>
            <span>
              {proposal.conflictSummary}
            </span>
          </div>
        )}
      </div>
      <div className="ai-crew-intent-review-actions">
        <Button
          type="primary"
          size="large"
          danger={Boolean(config.danger)}
          icon={isExecuting ? <LoadingOutlined /> : <ThunderboltFilled />}
          loading={isExecuting}
          onClick={handleClick}
          data-testid="intent-review-confirm-btn"
        >
          {isExecuting ? config.busy : confirmLabel}
        </Button>
        {conflicts.length > 0 && typeof onCancel === 'function' && (
          <Button size="large" disabled={isExecuting} onClick={onCancel} data-testid="intent-review-keep-btn">
            Keep existing schedule
          </Button>
        )}
      </div>
    </div>
  );
}
